import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LanguageContext } from "../App";
import { IMAGES } from "../translations";

const roomImages = [IMAGES.roomComfort, IMAGES.roomSuperior, IMAGES.roomEconomy];

export default function Rooms({ showBooking = false, hideHeader = false }) {
  const { t } = useContext(LanguageContext);

  return (
    <section
      id="rooms"
      data-testid="rooms-section"
      className="bg-[#F5F1EA] py-24 md:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {!hideHeader && (
        <div className="mb-14 md:mb-20 max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-4"
          >
            {t.rooms.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl md:text-5xl font-light text-[#1A1A19] tracking-tight mb-4"
            data-testid="rooms-headline"
          >
            {t.rooms.headline}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, delay: 0.2 }}
            className="font-['Manrope',sans-serif] text-sm text-[#6B6A68] leading-relaxed"
            data-testid="rooms-subtext"
          >
            {t.rooms.subtext}
          </motion.p>
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
          {t.rooms.items.map((room, i) => (
            <motion.div
              key={room.name}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: i * 0.15 }}
              className="group flex flex-col gap-5"
              data-testid={`room-card-${room.name.toLowerCase()}`}
            >
              <div className="overflow-hidden relative aspect-[4/5] w-full">
                <img
                  src={roomImages[i]} alt={room.name}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out ${
                    i === 0 ? "object-right" : "object-center"
                  }`}
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute bottom-5 left-5 font-['Cormorant_Garamond',serif] text-2xl font-light text-white tracking-wide">
                  {room.name}
                </span>
              </div>
              <div className="px-1 flex flex-col flex-1">
                <p className="font-['Manrope',sans-serif] text-sm text-[#6B6A68] leading-relaxed mb-4 line-clamp-2">
                  {room.tagline}
                </p>
                {showBooking ? (
                  <Link
                    to="/prenota"
                    data-testid={`room-book-${room.name.toLowerCase()}`}
                    className="inline-flex items-center gap-2 font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#1A1A19] hover:gap-3 transition-all duration-300"
                  >
                    {t.rooms.cta} <ArrowRight size={13} strokeWidth={1.5} />
                  </Link>
                ) : (
                  <Link
                    to="/camere"
                    data-testid={`room-explore-${room.name.toLowerCase()}`}
                    className="inline-flex items-center gap-2 font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#1A1A19] hover:gap-3 transition-all duration-300"
                  >
                    {t.rooms.explore || "Esplora"} <ArrowRight size={13} strokeWidth={1.5} />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
